import { fcDateToYmd } from './calendarUrlState';

export type AgendaGroupTone = 'past' | 'overdue' | 'today' | 'upcoming';

/** Champs minimaux pour grouper une échéance dans l’agenda. */
export interface AgendaGroupable {
  start: string;
  allDay: boolean;
  kind?: string;
}

export interface AgendaGroup<T extends AgendaGroupable = AgendaGroupable> {
  key: string;
  tone: AgendaGroupTone;
  items: T[];
}

function dayKey(start: string): string {
  if (!start.includes('T')) return start.slice(0, 10);
  return fcDateToYmd(new Date(start));
}

function isTimedPast(event: AgendaGroupable, now: Date): boolean {
  if (event.allDay || !event.start.includes('T')) return false;
  return new Date(event.start).getTime() < now.getTime();
}

/**
 * Groupes chrono d’un agenda : en retard, aujourd’hui, puis un groupe par jour.
 * `now` est injecté — pas d’horloge implicite.
 */
export function groupAgendaItems<T extends AgendaGroupable>(
  events: T[],
  now: Date,
): AgendaGroup<T>[] {
  const today = fcDateToYmd(now);
  const past: T[] = [];
  const overdue: T[] = [];
  const todayItems: T[] = [];
  const upcomingByDay = new Map<string, T[]>();

  const sorted = [...events].sort((a, b) => a.start.localeCompare(b.start));

  for (const event of sorted) {
    const key = dayKey(event.start);
    if (key < today || (key === today && isTimedPast(event, now))) {
      if (event.kind === 'hearing') past.push(event);
      else overdue.push(event);
      continue;
    }
    if (key === today) {
      todayItems.push(event);
      continue;
    }
    const bucket = upcomingByDay.get(key) ?? [];
    bucket.push(event);
    upcomingByDay.set(key, bucket);
  }

  const groups: AgendaGroup<T>[] = [];
  if (past.length > 0) {
    groups.push({ key: 'past', tone: 'past', items: past });
  }
  if (overdue.length > 0) {
    groups.push({ key: 'overdue', tone: 'overdue', items: overdue });
  }
  if (todayItems.length > 0) {
    groups.push({ key: 'today', tone: 'today', items: todayItems });
  }
  const upcomingDays = [...upcomingByDay.keys()].sort();
  for (const day of upcomingDays) {
    groups.push({
      key: day,
      tone: 'upcoming',
      items: upcomingByDay.get(day) ?? [],
    });
  }
  return groups;
}

/** Les N prochaines échéances, même ordre que l’agenda (retard → aujourd’hui → suite). */
export function takeNextAgendaItems<T extends AgendaGroupable>(
  events: T[],
  now: Date,
  limit: number,
): T[] {
  return groupAgendaItems(events, now)
    .filter((g) => g.tone !== 'past')
    .flatMap((g) => g.items)
    .slice(0, limit);
}
